// ============================================
// The Gilded Cage — Stage Performance Minigame
// (0.3 cabaret venue, CABARET_VENUE.md §4)
// Timed crowd-call routine: 5 beats, the crowd
// calls a move, answer it in time. Score maps to
// a 0-3 performance tier; Ink scales the shift
// pay with it (Cabaret_ShiftResults).
// Sibling of velvet-minigame.js (class shape,
// waitForInput/releaseInput handoff) crossed
// with bar-minigame's timed pattern.
// ============================================

import { events, Events } from '../engine/events.js';
import { storyManager } from '../engine/story.js';
import { variableManager } from '../engine/variables.js';
import { L } from '../systems/localization.js';

// ── UI tag value this module listens for ────────────────────────────────
const UI_MODE = 'cabaret_stage';

// ── Routine definitions (ids match Ink cabaret_current_routine 1-4) ────
const ROUTINES = [
    { id: 1, key: 'tease',     name: 'Classic Tease',  flow: ['tease', 'hips'] },
    { id: 2, key: 'chair',     name: 'Chair Routine',  flow: ['pose', 'tease'] },
    { id: 3, key: 'fans',      name: 'Fan Dance',      flow: ['spin', 'pose'] },
    { id: 4, key: 'spotlight', name: 'Spotlight Slow', flow: ['hips', 'pose'] },
];

// ── Move definitions ────────────────────────────────────────────────────
const MOVES = [
    { id: 'pose',  icon: '🌹' },   // 🌹
    { id: 'hips',  icon: '💃' },   // 💃
    { id: 'tease', icon: '🧤' },   // 🧤
    { id: 'spin',  icon: '🌀' },   // 🌀
];

// ── Crowd-call flavor lines per move (rotated) ──────────────────────────
const CALL_LINES = {
    pose:  ['"Hold it right there, gorgeous!"', 'The front row wants a pose.', 'Someone shouts for a pin-up hold.'],
    hips:  ['"Work those hips!"', 'The brass swings low. Hips.', 'A table starts clapping a slow rhythm.'],
    tease: ['"The glove! The glove!"', 'They want the tease drawn out.', 'A whistle asks for less, slower.'],
    spin:  ['"Spin for us, doll!"', 'The room wants you turning.', 'The drummer rolls. That means spin.'],
};

const HIT_LINES     = ['The room roars.', 'Bills hit the apron.', 'Exactly what they wanted.'];
const FLOW_LINES    = ['Smooth, on-theme. They stay with you.', 'Not what they called, but it works.', 'The routine carries it.'];
const MISS_LINES    = ['The beat lands wrong. Drinks resume.', 'A cough somewhere at the back.', 'You feel the room slip an inch.'];
const TIMEOUT_LINES = ['You hesitate. The moment passes.', 'Too slow. The call dies out.', 'The band covers for you.'];

// ── Late-show (explicit) ruleset — Cabaret_LateShow_Stage sets
// cabaret_late_mode before the #ui tag. Beat 1 stays a classic call; beats
// 2-5 escalate through LATE_FLOW. The player SERVES the call (2 pts, +heat)
// or deflects with a routine-flow classic move (1 pt, composure kept).
// Heat >= 100 mid-set = ruined moment (spontaneous orgasm): the set ends,
// JS writes cabaret_mg_ruined and Ink routes the NSFW outcome.
const LATE_FLOW = ['strip', 'flash', 'grind', 'touch'];
const LATE_MOVES = {
    strip: { icon: '👙', label: 'Strip' },
    flash: { icon: '✨', label: 'Flash' },
    grind: { icon: '🔥', label: 'Grind' },
    touch: { icon: '🖐️', label: 'Touch' },
};
const LATE_CALL_LINES = {
    strip: ['"Lose the top, doll!"', 'The back table starts a chant: off, off, off.', 'A bill held high, pointed at your top.'],
    flash: ['"Show us the rest!"', 'A low whistle asks for what the skirt is hiding.', '"You know what we stayed for!"'],
    grind: ['"Give the floor some love!"', 'The brass grinds low. They want you lower.', 'A voice from the dark: "Slower. Lower."'],
    touch: ['"Hands, sweetheart. Use them."', 'They want your hands where the light is.', '"Touch. We tip for touch."'],
};
const SERVE_LINES   = ['The room makes a sound the evening crowd never makes.', 'Bills land before the move is even finished.', 'Twenty people forget their drinks at once.'];
const DEFLECT_LINES = ['You give them craft instead. They allow it.', 'The routine holds the line for you.', 'A knowing laugh from the dark: they will ask again.'];

const HEAT_PER_SERVE = 14;          // heat added per served explicit call
const HEAT_PER_SERVE_CAGED = 18;    // the cage presses back — riskier serves
const HEAT_RUIN = 100;              // heat threshold for the ruined moment
const HEAT_SEED_MAX = 95;           // ruin is always opt-in: >= 1 serve needed

// ── Score thresholds → performance tier ─────────────────────────────────
// 5 beats × 2 max = 10, +1 one-shot Reveal bonus (exhib_fem 25+). Answering
// every call = Showstopper; riding the routine flow all night = Passable.
const PERFORMANCE_TIERS = [
    { min: 0, tier: 0, labelKey: 'cabaret_mg.tier.0', label: 'Stumbling',   multiplier: 0.5, stars: 1 },
    { min: 4, tier: 1, labelKey: 'cabaret_mg.tier.1', label: 'Passable',    multiplier: 1.0, stars: 2 },
    { min: 7, tier: 2, labelKey: 'cabaret_mg.tier.2', label: 'Captivating', multiplier: 1.5, stars: 4 },
    { min: 9, tier: 3, labelKey: 'cabaret_mg.tier.3', label: 'Showstopper', multiplier: 2.0, stars: 5 },
];

const BEATS = 5;                 // beats per set
const BEAT_MS = 3600;            // time to answer a call
const CALL_FLOW_BIAS = 0.6;      // chance the call comes from the routine's flow set
const BASE_TIP = 30;             // display-only; Ink computes real pay
const REVEAL_UNLOCK_EXHIB = 25;  // exhib_fem tier 1+ unlocks the one-shot Reveal

// ============================================================================
// CabaretMinigame Class
// ============================================================================

class CabaretMinigame {
    constructor() {
        this._root = null;
        this._isActive = false;
        this._routine = null;
        this._score = 0;
        this._beat = 0;              // 1-based current beat
        this._calledMove = null;
        this._beatOpen = false;      // accepting input for the current beat
        this._beatTimer = null;
        this._revealAvailable = false;
        this._revealUsed = false;
        this._lateMode = false;      // Late Show ruleset (cabaret_late_mode)
        this._served = 0;            // explicit calls served this set
        this._heat = 0;              // arousal pressure; >= HEAT_RUIN ruins
        this._ruined = false;        // spontaneous orgasm ended the set
        this._caged = false;         // wearing_chastity snapshot at set start

        this._scope = events.createScope('cabaret-minigame');
        this._injectStyles();
        this._bindEvents();
    }

    // ── Public API ──────────────────────────────────────────────────────

    isActive() {
        return this._isActive;
    }

    destroy() {
        this._clearBeatTimer();
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
    }

    // ── Event Bindings ──────────────────────────────────────────────────

    _bindEvents() {
        this._scope.on(Events.TAG_UI, (mode) => {
            if (mode === UI_MODE) {
                this._activate();
            } else if (this._isActive) {
                this._deactivate(false);
            }
        });
    }

    // ── Activation / Deactivation ───────────────────────────────────────

    _activate() {
        let routineId = 1;
        let exhib = 0;
        let lateMode = false;
        let heatSeed = 0;
        try {
            const story = storyManager.currentStory;
            if (story) {
                // Defensive: the venue is kill-switch-gated in Ink; if the tag
                // somehow fires while dormant, don't trap the story.
                if (!story.variablesState['v030_content_enabled']) return;
                routineId = story.variablesState['cabaret_current_routine'] || 1;
                const e = story.variablesState['exhib_fem'];
                if (typeof e === 'number') exhib = e;
                lateMode = !!story.variablesState['cabaret_late_mode'];
                // Heat starts where the player's Arousal already is — going
                // into the late show worked up IS the push-your-luck stake.
                const a = story.variablesState['Arousal'];
                if (typeof a === 'number') {
                    heatSeed = Math.max(0, Math.min(HEAT_SEED_MAX, Math.round(a)));
                }
            }
        } catch(e) {
            console.warn('[CabaretMinigame] Could not read cabaret vars:', e);
        }

        this._routine = ROUTINES.find(r => r.id === routineId) || ROUTINES[0];
        this._score = 0;
        this._beat = 0;
        this._calledMove = null;
        this._beatOpen = false;
        this._lateMode = lateMode;
        this._served = 0;
        this._heat = lateMode ? heatSeed : 0;
        this._ruined = false;
        this._caged = !!variableManager.getFlag('wearing_chastity');
        // The one-shot Reveal is the classic set's toe over the line — the
        // late show IS the line, so it's serve buttons instead.
        this._revealAvailable = exhib >= REVEAL_UNLOCK_EXHIB && !lateMode;
        this._revealUsed = false;
        this._isActive = true;

        // Pause story processing until the set completes
        storyManager.waitForInput();

        if (!this._root) {
            this._createContainer();
        }

        // Hide #story while the minigame owns the center (velvet pattern —
        // otherwise the prior scene stays visible below the panel).
        const story = document.getElementById('story');
        if (story) {
            this._storyPrevDisplay = story.style.display || '';
            story.style.display = 'none';
        }

        this._buildUI();
        this._root.classList.remove('hidden');
        this._startBeat();
    }

    _deactivate(resume = true) {
        this._isActive = false;
        this._clearBeatTimer();
        if (this._root) {
            this._root.classList.add('hidden');
        }
        const story = document.getElementById('story');
        if (story) story.style.display = this._storyPrevDisplay || '';
        if (resume) {
            storyManager.releaseInput();
        }
    }

    _clearBeatTimer() {
        if (this._beatTimer) {
            clearTimeout(this._beatTimer);
            this._beatTimer = null;
        }
    }

    // ── DOM Construction ────────────────────────────────────────────────

    _createContainer() {
        const el = document.createElement('div');
        el.id = 'cabaret-stage-panel';
        el.className = 'cabaret-stage-panel hidden';
        const story = document.getElementById('story');
        if (story) {
            story.parentNode.insertBefore(el, story);
        } else {
            document.body.appendChild(el);
        }
        this._root = el;
    }

    _buildUI() {
        this._root.innerHTML = '';

        // ── Header: routine name + beat counter ─────────────────────────
        const header = document.createElement('div');
        header.className = 'cabaret-header';
        const venueLabel = this._lateMode
            ? L.get('cabaret_mg.title_late', 'The Gilded Cage — After Hours')
            : L.get('cabaret_mg.title', 'The Gilded Cage');
        header.innerHTML = `
            <div class="cabaret-title">
                <div class="cabaret-venue">${venueLabel}</div>
                <div class="cabaret-routine">${L.get('cabaret_mg.routine.' + this._routine.key, this._routine.name)}</div>
            </div>
            <div class="cabaret-beats" id="cabaret-beats">${L.get('cabaret_mg.beat', 'Beat')} <span id="cabaret-beat-count">1</span>/${BEATS}</div>
        `;
        this._root.appendChild(header);

        // ── Stage image (appearance variants applied) ────────────────────
        const stageImage = document.createElement('div');
        stageImage.className = 'cabaret-stage-image';
        stageImage.id = 'cabaret-stage-image';
        const imgUrl = this._resolveStageImage();
        if (imgUrl) {
            stageImage.innerHTML = `<img src="${imgUrl}" alt="stage" class="cabaret-stage-img-el">`;
        }
        this._root.appendChild(stageImage);

        // ── Crowd call line ──────────────────────────────────────────────
        const call = document.createElement('div');
        call.className = 'cabaret-call';
        call.id = 'cabaret-call';
        call.textContent = L.get('cabaret_mg.intro', 'The footlights come up. Watch the crowd, answer the call...');
        this._root.appendChild(call);

        // ── Beat timer bar ───────────────────────────────────────────────
        const timerWrap = document.createElement('div');
        timerWrap.className = 'cabaret-timer';
        timerWrap.innerHTML = '<div class="cabaret-timer-fill" id="cabaret-timer-fill"></div>';
        this._root.appendChild(timerWrap);

        // ── Serve button (late mode: answer the explicit call) ──────────
        if (this._lateMode) {
            const serve = document.createElement('button');
            serve.id = 'cabaret-serve-btn';
            serve.className = 'cabaret-serve-btn hidden';
            serve.addEventListener('click', () => this._onServeClick());
            this._root.appendChild(serve);
        }

        // ── Move buttons ─────────────────────────────────────────────────
        const moves = document.createElement('div');
        moves.className = 'cabaret-moves';
        for (const move of MOVES) {
            const btn = document.createElement('button');
            btn.className = 'cabaret-move-btn';
            btn.dataset.move = move.id;
            btn.innerHTML = `<span class="cabaret-move-icon">${move.icon}</span><span class="cabaret-move-label">${L.get('cabaret_mg.move.' + move.id, move.id.charAt(0).toUpperCase() + move.id.slice(1))}</span>`;
            btn.addEventListener('click', () => this._onMoveClick(move.id));
            moves.appendChild(btn);
        }
        this._root.appendChild(moves);

        // ── One-shot Reveal (exhibition unlock) ──────────────────────────
        if (this._revealAvailable) {
            const reveal = document.createElement('button');
            reveal.id = 'cabaret-reveal-btn';
            reveal.className = 'cabaret-reveal-btn';
            reveal.innerHTML = `<span class="cabaret-move-icon">✨</span> ${L.get('cabaret_mg.move.reveal', 'Reveal')}`;
            reveal.addEventListener('click', () => this._onReveal());
            this._root.appendChild(reveal);
        }

        // ── Crowd meter ──────────────────────────────────────────────────
        const meter = document.createElement('div');
        meter.className = 'cabaret-meter';
        meter.innerHTML = `
            <div class="cabaret-meter-label">${L.get('cabaret_mg.crowd', 'Crowd')}</div>
            <div class="cabaret-meter-track"><div class="cabaret-meter-fill" id="cabaret-meter-fill" style="width:0%"></div></div>
            <div class="cabaret-meter-tier" id="cabaret-meter-tier"></div>
        `;
        this._root.appendChild(meter);

        // ── Heat meter (late mode: ruin pressure) ────────────────────────
        if (this._lateMode) {
            const heat = document.createElement('div');
            heat.className = 'cabaret-meter cabaret-heat';
            heat.innerHTML = `
                <div class="cabaret-meter-label">${L.get('cabaret_mg.heat', 'Heat')}</div>
                <div class="cabaret-meter-track"><div class="cabaret-meter-fill cabaret-heat-fill" id="cabaret-heat-fill" style="width:0%"></div></div>
                <div class="cabaret-meter-tier cabaret-heat-tier" id="cabaret-heat-tier"></div>
            `;
            this._root.appendChild(heat);
            this._updateHeat();
        }
    }

    // ── Image resolution (velvet pattern: manifest + appearance suffixes) ─

    _resolveImage(name) {
        if (!name) return null;
        const manifest = window._imageManifest;
        if (manifest && manifest[name] !== undefined) {
            const folder = manifest[name];
            const base = folder ? `assets/images/${folder}/${name}` : `assets/images/${name}`;
            return `${base}.png`;
        }
        return null;
    }

    _resolveStageImage() {
        const wearingChastity = !!variableManager.getFlag('wearing_chastity');
        const longHair = !!variableManager.getFlag('has_long_hair');
        // Late mode: served explicit calls strip the stage image down beat
        // by beat (base → topless → bare).
        let base = 'cabaret_stage_spotlight';
        if (this._lateMode && this._served >= 3) {
            base = 'cabaret_stage_late_bare';
        } else if (this._lateMode && this._served >= 1) {
            base = 'cabaret_stage_late_topless';
        }
        const candidates = [];
        if (wearingChastity && longHair) candidates.push(`${base}_chastity_longhair`);
        if (wearingChastity) candidates.push(`${base}_chastity`);
        if (longHair) candidates.push(`${base}_longhair`);
        candidates.push(base);
        for (const name of candidates) {
            const url = this._resolveImage(name);
            if (url) return url;
        }
        return null;
    }

    _refreshStageImage() {
        const holder = document.getElementById('cabaret-stage-image');
        if (!holder) return;
        const url = this._resolveStageImage();
        if (!url) return; // art not generated yet — keep the current frame
        const img = holder.querySelector('.cabaret-stage-img-el');
        if (img) {
            if (!img.src.endsWith(url)) img.src = url;
        } else {
            holder.innerHTML = `<img src="${url}" alt="stage" class="cabaret-stage-img-el">`;
        }
    }

    // ── Beat lifecycle ──────────────────────────────────────────────────

    _startBeat() {
        if (!this._isActive) return;
        if (this._beat >= BEATS) {
            this._finishSession();
            return;
        }
        this._beat++;
        this._beatOpen = true;

        const isExplicitBeat = this._lateMode && this._beat >= 2;
        if (isExplicitBeat) {
            // Explicit escalation ladder: fixed order, one call per beat
            this._calledMove = LATE_FLOW[(this._beat - 2) % LATE_FLOW.length];
        } else {
            // Pick the crowd call: biased toward the routine's flow moves
            let pool;
            if (Math.random() < CALL_FLOW_BIAS) {
                pool = this._routine.flow;
            } else {
                pool = MOVES.map(m => m.id);
            }
            this._calledMove = pool[Math.floor(Math.random() * pool.length)];
        }

        // Update beat counter + call line
        const countEl = document.getElementById('cabaret-beat-count');
        if (countEl) countEl.textContent = String(this._beat);
        const lines = (isExplicitBeat ? LATE_CALL_LINES : CALL_LINES)[this._calledMove] || ['...'];
        this._setCall(lines[Math.floor(Math.random() * lines.length)], 'call');

        // Arm / hide the serve button for this beat
        const serveBtn = document.getElementById('cabaret-serve-btn');
        if (serveBtn) {
            if (isExplicitBeat) {
                const mv = LATE_MOVES[this._calledMove];
                serveBtn.innerHTML = `<span class="cabaret-move-icon">${mv.icon}</span> ${L.get('cabaret_mg.serve', 'Serve the call')}: ${L.get('cabaret_mg.late.' + this._calledMove, mv.label)}`;
                serveBtn.classList.remove('hidden');
                serveBtn.disabled = false;
            } else {
                serveBtn.classList.add('hidden');
            }
        }

        // Re-enable move buttons
        this._root.querySelectorAll('.cabaret-move-btn').forEach(btn => {
            btn.disabled = false;
            btn.classList.remove('cabaret-move-dim');
        });

        // Restart the timer bar animation
        const fill = document.getElementById('cabaret-timer-fill');
        if (fill) {
            fill.style.transition = 'none';
            fill.style.width = '100%';
            void fill.offsetWidth; // reflow to restart the transition
            fill.style.transition = `width ${BEAT_MS}ms linear`;
            fill.style.width = '0%';
        }

        this._clearBeatTimer();
        this._beatTimer = setTimeout(() => this._onBeatTimeout(), BEAT_MS);
    }

    _endBeat() {
        this._beatOpen = false;
        this._clearBeatTimer();
        this._root.querySelectorAll('.cabaret-move-btn').forEach(btn => {
            btn.disabled = true;
            btn.classList.add('cabaret-move-dim');
        });
        const serveBtn = document.getElementById('cabaret-serve-btn');
        if (serveBtn) serveBtn.disabled = true;
        this._updateMeter();
        // Short breather so the reaction line lands, then next beat / finish
        setTimeout(() => {
            if (this._isActive) this._startBeat();
        }, 900);
    }

    _onBeatTimeout() {
        if (!this._isActive || !this._beatOpen) return;
        this._setCall(TIMEOUT_LINES[Math.floor(Math.random() * TIMEOUT_LINES.length)], 'miss');
        this._endBeat();
    }

    _onMoveClick(moveId) {
        if (!this._isActive || !this._beatOpen) return;
        let points = 0;
        let lineSet = MISS_LINES;
        let mood = 'miss';
        if (moveId === this._calledMove) {
            points = 2;
            lineSet = HIT_LINES;
            mood = 'hit';
        } else if (this._routine.flow.includes(moveId)) {
            points = 1;
            // On an explicit call, a routine-flow move is a deflection: the
            // crowd wanted more, the craft buys you a pass.
            lineSet = (this._lateMode && this._beat >= 2) ? DEFLECT_LINES : FLOW_LINES;
            mood = 'flow';
        }
        this._score += points;
        this._setCall(lineSet[Math.floor(Math.random() * lineSet.length)], mood);
        this._endBeat();
    }

    _onServeClick() {
        if (!this._isActive || !this._beatOpen || !this._lateMode) return;
        this._score += 2;
        this._served += 1;
        this._heat += this._caged ? HEAT_PER_SERVE_CAGED : HEAT_PER_SERVE;
        this._refreshStageImage();
        this._updateHeat();
        if (this._heat >= HEAT_RUIN) {
            this._ruinMoment();
            return;
        }
        this._setCall(SERVE_LINES[Math.floor(Math.random() * SERVE_LINES.length)], 'hit');
        this._endBeat();
    }

    // The heat wins mid-set: spontaneous orgasm under the spotlight. The set
    // is over whether the player agrees or not — Ink routes the NSFW outcome
    // via cabaret_mg_ruined.
    _ruinMoment() {
        this._ruined = true;
        this._beatOpen = false;
        this._clearBeatTimer();
        this._root.querySelectorAll('.cabaret-move-btn').forEach(btn => {
            btn.disabled = true;
            btn.classList.add('cabaret-move-dim');
        });
        const serveBtn = document.getElementById('cabaret-serve-btn');
        if (serveBtn) serveBtn.disabled = true;
        this._setCall(L.get('cabaret_mg.ruined_line', 'The heat wins. Right there, under the light, in front of all of them.'), 'hit');
        this._updateMeter();
        setTimeout(() => {
            if (this._isActive) this._finishSession();
        }, 1400);
    }

    _onReveal() {
        if (!this._isActive || this._revealUsed) return;
        this._revealUsed = true;
        this._score += 1;
        const btn = document.getElementById('cabaret-reveal-btn');
        if (btn) {
            btn.disabled = true;
            btn.classList.add('cabaret-reveal-used');
        }
        this._setCall(L.get('cabaret_mg.reveal_line', 'A flash of skin, perfectly timed. The room gasps.'), 'hit');
        this._updateMeter();
    }

    // ── Display helpers ─────────────────────────────────────────────────

    _setCall(text, mood) {
        const el = document.getElementById('cabaret-call');
        if (!el) return;
        el.className = 'cabaret-call';
        void el.offsetWidth;
        el.textContent = text;
        el.classList.add('cabaret-call-enter');
        if (mood === 'hit') el.classList.add('cabaret-call-hit');
        else if (mood === 'flow') el.classList.add('cabaret-call-flow');
        else if (mood === 'miss') el.classList.add('cabaret-call-miss');
    }

    _getTier() {
        for (let i = PERFORMANCE_TIERS.length - 1; i >= 0; i--) {
            if (this._score >= PERFORMANCE_TIERS[i].min) {
                return PERFORMANCE_TIERS[i];
            }
        }
        return PERFORMANCE_TIERS[0];
    }

    _updateMeter() {
        const maxScore = BEATS * 2 + 1;
        const pct = Math.min(100, Math.round((this._score / maxScore) * 100));
        const fill = document.getElementById('cabaret-meter-fill');
        if (fill) fill.style.width = `${pct}%`;
        const tierEl = document.getElementById('cabaret-meter-tier');
        if (tierEl) {
            const tier = this._getTier();
            tierEl.textContent = L.get(tier.labelKey, tier.label);
        }
    }

    _updateHeat() {
        const fill = document.getElementById('cabaret-heat-fill');
        if (fill) fill.style.width = `${Math.min(100, this._heat)}%`;
        const label = document.getElementById('cabaret-heat-tier');
        if (label) {
            if (this._heat >= 85) {
                label.textContent = L.get('cabaret_mg.heat.edge', 'On the edge');
            } else if (this._heat >= 60) {
                label.textContent = L.get('cabaret_mg.heat.hot', 'Burning');
            } else if (this._heat >= 30) {
                label.textContent = L.get('cabaret_mg.heat.warm', 'Warm');
            } else {
                label.textContent = '';
            }
        }
    }

    // ── Session Complete ────────────────────────────────────────────────

    _finishSession() {
        const tier = this._getTier();
        const tipAmount = Math.round(BASE_TIP * tier.multiplier);

        if (this._ruined) {
            this._setCall(L.get('cabaret_mg.ruined_summary', 'The room saw everything. The apron fills anyway.'), 'hit');
        } else {
            this._setCall(
                `${L.get(tier.labelKey, tier.label)}. ${L.get('cabaret_mg.tips', 'Stage tips')}: $${tipAmount}`,
                tier.tier >= 2 ? 'hit' : (tier.tier >= 1 ? 'flow' : 'miss')
            );
        }
        this._updateMeter();

        // Freeze the board
        this._root.querySelectorAll('.cabaret-move-btn').forEach(btn => {
            btn.disabled = true;
            btn.classList.add('cabaret-move-dim');
        });
        const reveal = document.getElementById('cabaret-reveal-btn');
        if (reveal) {
            reveal.disabled = true;
            reveal.classList.add('cabaret-reveal-used');
        }

        // Write results to Ink. Non-observed vars → Ink-only writes
        // (story.variablesState), per save-state-drift rule 1.
        try {
            const story = storyManager.currentStory;
            if (story) {
                story.variablesState['cabaret_performance'] = tier.tier;
                story.variablesState['cabaret_tip_amount'] = tipAmount;
                story.variablesState['cabaret_mg_served'] = this._served;
                story.variablesState['cabaret_mg_ruined'] = !!this._ruined;
            }
        } catch(e) {
            console.warn('[CabaretMinigame] Could not write results to Ink:', e);
        }

        // Let the summary land, then hand back to the story
        setTimeout(() => {
            this._deactivate(true);
        }, 1500);
    }

    // ── CSS Injection ───────────────────────────────────────────────────

    _injectStyles() {
        if (document.getElementById('cabaret-minigame-styles')) return;

        const style = document.createElement('style');
        style.id = 'cabaret-minigame-styles';
        style.textContent = `
/* ============================================
   Gilded Cage Stage Minigame Panel
   ============================================ */

.cabaret-stage-panel {
    background: rgba(20, 13, 10, 0.95);
    border: 1px solid rgba(224, 178, 92, 0.18);
    border-radius: 12px;
    padding: 12px 14px 14px;
    margin: 8px auto;
    max-width: 640px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(232, 196, 120, 0.06);
    /* Velvet lesson (Android clipping): allow shrink + own scrollbar on
       short viewports instead of silently clipping the bottom row. */
    flex: 0 1 auto;
    min-height: 0;
    max-height: 100%;
    overflow-y: auto;
    position: relative;
}

.cabaret-stage-panel.hidden {
    display: none;
}

/* ── Header ──────────────────────────────── */

.cabaret-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(224, 178, 92, 0.12);
}

.cabaret-venue {
    font-size: 11px;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    color: rgba(232, 196, 120, 0.75);
}

.cabaret-routine {
    font-size: 15px;
    font-weight: 600;
    color: var(--text, #ddd4ec);
}

.cabaret-beats {
    font-size: 12px;
    color: var(--text-dim, #a89ab8);
    white-space: nowrap;
}

.cabaret-beats span {
    color: rgba(232, 196, 120, 0.95);
    font-weight: 600;
}

/* ── Stage image ─────────────────────────── */

.cabaret-stage-image {
    width: 100%;
    max-width: 520px;
    aspect-ratio: 4 / 3;
    margin: 0 auto;
    background: rgba(28, 18, 12, 0.6);
    border: 1px solid rgba(224, 178, 92, 0.16);
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
}

.cabaret-stage-image:empty {
    display: none;
}

.cabaret-stage-img-el {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

/* ── Crowd call line ─────────────────────── */

.cabaret-call {
    min-height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13.5px;
    font-style: italic;
    color: var(--text-body, #c8beda);
    text-align: center;
    padding: 8px 12px;
    border-radius: 8px;
    background: rgba(34, 22, 14, 0.6);
}

.cabaret-call-enter {
    animation: cabaret-fade-in 0.3s ease-out;
}

.cabaret-call-hit  { color: #e8c860; }
.cabaret-call-flow { color: var(--rose, #e88cb8); }
.cabaret-call-miss { color: var(--text-faint, #706480); }

@keyframes cabaret-fade-in {
    from { opacity: 0; transform: translateY(4px); }
    to   { opacity: 1; transform: translateY(0); }
}

/* ── Beat timer ──────────────────────────── */

.cabaret-timer {
    height: 6px;
    border-radius: 3px;
    background: rgba(60, 42, 26, 0.6);
    overflow: hidden;
}

.cabaret-timer-fill {
    height: 100%;
    width: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, rgba(232, 196, 120, 0.9), rgba(228, 140, 184, 0.85));
}

/* ── Move buttons ────────────────────────── */

.cabaret-moves {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}

.cabaret-move-btn {
    min-height: 52px;
    padding: 10px 6px;
    background: linear-gradient(145deg, rgba(44, 30, 20, 0.92), rgba(30, 20, 14, 0.95));
    border: 1px solid rgba(88, 62, 38, 0.7);
    border-radius: 8px;
    color: var(--text, #ddd4ec);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    transition: all 0.18s ease;
}

.cabaret-move-btn:hover:not(:disabled) {
    border-color: rgba(232, 196, 120, 0.5);
    box-shadow: 0 4px 18px rgba(232, 196, 120, 0.12);
    transform: translateY(-2px);
}

.cabaret-move-btn:active:not(:disabled) {
    transform: scale(0.97);
}

.cabaret-move-btn.cabaret-move-dim {
    opacity: 0.35;
    pointer-events: none;
    transform: none;
}

.cabaret-move-icon {
    font-size: 20px;
    line-height: 1;
}

.cabaret-move-label {
    font-size: 11px;
    color: var(--text-dim, #a89ab8);
    letter-spacing: 0.3px;
}

/* ── Reveal button ───────────────────────── */

.cabaret-reveal-btn {
    align-self: center;
    padding: 7px 22px;
    background: linear-gradient(145deg, rgba(96, 28, 56, 0.55), rgba(60, 18, 38, 0.65));
    border: 1px solid rgba(255, 168, 196, 0.4);
    border-radius: 18px;
    color: rgba(255, 230, 240, 0.92);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.18s ease;
}

.cabaret-reveal-btn:hover:not(:disabled) {
    border-color: rgba(255, 168, 196, 0.7);
    box-shadow: 0 0 14px rgba(255, 168, 196, 0.18);
}

.cabaret-reveal-btn.cabaret-reveal-used {
    opacity: 0.3;
    pointer-events: none;
}

/* ── Crowd meter ─────────────────────────── */

.cabaret-meter {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-top: 8px;
    border-top: 1px solid rgba(224, 178, 92, 0.12);
}

.cabaret-meter-label {
    font-size: 11px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--text-dim, #a89ab8);
    flex-shrink: 0;
}

.cabaret-meter-track {
    flex: 1;
    height: 8px;
    border-radius: 4px;
    background: rgba(60, 42, 26, 0.6);
    overflow: hidden;
}

.cabaret-meter-fill {
    height: 100%;
    border-radius: 4px;
    background: linear-gradient(90deg, rgba(228, 140, 184, 0.85), rgba(232, 196, 120, 0.95));
    transition: width 0.4s ease;
}

.cabaret-meter-tier {
    font-size: 12px;
    font-weight: 600;
    color: rgba(232, 196, 120, 0.9);
    min-width: 84px;
    text-align: right;
}

/* ── Late show: serve button + heat meter ── */

.cabaret-serve-btn {
    min-height: 48px;
    padding: 10px 14px;
    background: linear-gradient(145deg, rgba(150, 32, 82, 0.75), rgba(96, 18, 52, 0.85));
    border: 1px solid rgba(255, 122, 178, 0.55);
    border-radius: 10px;
    color: rgba(255, 234, 244, 0.96);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.18s ease;
}

.cabaret-serve-btn.hidden {
    display: none;
}

.cabaret-serve-btn:hover:not(:disabled) {
    border-color: rgba(255, 168, 196, 0.9);
    box-shadow: 0 0 18px rgba(255, 122, 178, 0.3);
    transform: translateY(-2px);
}

.cabaret-serve-btn:active:not(:disabled) {
    transform: scale(0.97);
}

.cabaret-serve-btn:disabled {
    opacity: 0.35;
    pointer-events: none;
}

.cabaret-heat-fill {
    background: linear-gradient(90deg, rgba(228, 140, 184, 0.7), rgba(255, 82, 140, 0.95));
}

.cabaret-heat-tier {
    color: rgba(255, 140, 180, 0.95);
}

/* ── Mobile: 2-column grid ───────────────── */

@media (max-width: 768px) {
    .cabaret-moves {
        grid-template-columns: repeat(2, 1fr);
    }

    .cabaret-stage-panel {
        padding: 12px;
        gap: 10px;
        margin: 4px 0;
    }

    .cabaret-move-btn {
        min-height: 54px;
    }
}
`;
        document.head.appendChild(style);
    }
}

// ── Singleton export ────────────────────────────────────────────────────
// Hooked up in main.js via import (constructor self-registers on TAG_UI).
export const cabaretMinigame = new CabaretMinigame();
